import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { CloseIcon, IconPluginSetting, PasswordIcon, SearchIcon } from '../iconSet/IconSet';
import InputGroup from '../inputComp/InputGroup';
import semver from 'semver'

import FloodThumb from '../../images/icons/flood-simulation-thum.jpg'
import TsunamiThumb from '../../images/icons/tsunami-and-storm-surge-thum.jpg'
import QGISThumb from '../../images/icons/qgis-thumb.jpg'
import ArcGISThumb from '../../images/icons/arcgis-thum.jpg'
import RiverAIThumb from '../../images/icons/river-ai-thumb.jpg'
import Mobilize from '../../images/icons/mobilize-thumb.jpg'
import GlobalWeatherAnalyzerThumb from '../../images/icons/global-weather-analyzer-thumb.jpg'
import AlertDialog from '../popupComp/AlertDialog';


type PluginDataType = {
    id: string;
    name: string;
    description: string;
    image: any;
    isInstalled: boolean;
    version: string;
    developer: string;
    url: string;
    enabled: boolean;
}

type VersionDataType = {
    id: string;
    latestVersion: string;
}

const PluginSettings = () => {

    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertCallback, setAlertCallback] = useState<() => void | undefined>();

    const showAlertDialog = (title: string, description: string, callback?: () => void) => {
    setAlertTitle(title);
    setAlertDescription(description);
    setAlertCallback(() => callback); // wrap in function to preserve reference
    setAlertDialogState(true);
    };

    const [pluginData, setPluginData] = useState<PluginDataType[]>([
        {
            id: "flood_simulation", 
            name: "Flood Simulation", 
            description: "The Flood Simulation Plugin is a powerful tool designed to model, visualize, and analyze flood scenarios using terrain data, rainfall inputs, hydrodynamic parameters, and user-defined infrastructure settings. It enables planners, engineers, and researchers to simulate water flow behavior across landscapes, assess flood risks, and develop mitigation strategies with high spatial and temporal precision.", 
            image: FloodThumb, 
            isInstalled: false, 
            version: '1.0.0',
            developer: "Tecxal Systems",
            url: "https://www.tecxal.com/",
            enabled: true,
        },
        {
            id: "tsunami_and_storm_surge", 
            name: "Tsunami And Storm Surge", 
            description: "The Tsunami and Storm Surge Simulation Plugin is a high-precision coastal hazard modeling tool designed to simulate the impact of tsunamis and storm surges on coastal and near-shore areas. Using bathymetric data, tide levels, wind forcing, and earthquake parameters, it helps coastal engineers, emergency responders, and climate scientists assess inundation risks and develop robust disaster preparedness strategies.", 
            image: TsunamiThumb, 
            isInstalled: false, 
            version: '1.0.0',
            developer: "Tecxal Systems",
            url: "https://www.tecxal.com/",
            enabled: false,
        },
        {
            id: "qgis", 
            name: "QGIS", 
            description: "A versatile plugin that enhances the capabilities of QGIS with custom tools for spatial analysis, data processing, and visualization. This plugin serves as a foundational toolkit, offering utility functions and geospatial automation features designed to streamline your GIS workflows.", 
            image: QGISThumb, 
            isInstalled: false, 
            version: '1.0.0',
            developer: "Tecxal Systems",
            url: "https://www.tecxal.com/",
            enabled: false,
        },
        {
            id: "arc_gis", 
            name: "ArcGIS", 
            description: "The ArcGIS plugin provides seamless integration with ArcGIS services and tools, allowing users to access, process, and visualize ArcGIS-hosted data directly from their environment. This plugin bridges workflows between ArcGIS and other GIS platforms, enhancing interoperability and spatial analysis capabilities.", 
            image: ArcGISThumb, 
            isInstalled: false, 
            version: '1.0.0',
            developer: "Tecxal Systems",
            url: "https://www.tecxal.com/",
            enabled: false,
        },
        {
            id: "river_prediction_ai", 
            name: "River Prediction AI", 
            description: "River Prediction AI is an intelligent plugin that uses machine learning and hydrological modeling to forecast river behavior, including water level changes, flood risks, and flow rates. It supports early warning systems and decision-making for disaster management, urban planning, and environmental monitoring.", 
            image: RiverAIThumb, 
            isInstalled: false, 
            version: '1.0.0',
            developer: "Tecxal Systems",
            url: "https://www.tecxal.com/",
            enabled: false,
        },
        {
            id: "mobilize", 
            name: "Mobilize", 
            description: "Mobilize is a geospatial plugin designed to support the coordination of field operations, resource deployment, and rapid response planning. It enables users to visualize, track, and optimize mobilization efforts during emergencies, campaigns, or infrastructure operations directly within a GIS environment.", 
            image: Mobilize, 
            isInstalled: false, 
            version: '1.0.0',
            developer: "Tecxal Systems",
            url: "https://www.tecxal.com/",
            enabled: false,
        },
        {
            id: "global_weather_analyzer", 
            name: "Global Weather Analyzer", 
            description: "Global Weather Analyzer is a powerful plugin designed to visualize, analyze, and interpret global weather data directly within your GIS environment. It provides comprehensive tools to access real-time and historical weather datasets, enabling users to monitor weather patterns, forecast trends, and support climate-related decision making worldwide.", 
            image: GlobalWeatherAnalyzerThumb, 
            isInstalled: false, 
            version: '1.0.0',
            developer: "Tecxal Systems",
            url: "https://www.tecxal.com/",
            enabled: false,
        }
    ])
    
    const versionsRef = useRef<VersionDataType[]>([
        {id: "flood_simulation", latestVersion: "1.0.0"},
        {id: "tsunami_and_storm_surge", latestVersion: "1.1.0"},
        {id: "qgis", latestVersion: "1.0.0"},
        {id: "arc_gis", latestVersion: "2.0.0"},
        {id: "river_prediction_ai", latestVersion: "1.0.0"},
        {id: "mobilize", latestVersion: "1.0.0"},
        {id: "global_weather_analyzer", latestVersion: "1.0.0"},
    ])

    const [pluginDataFilter, setPluginDataFilter] = useState<PluginDataType[]>(pluginData)

    // Const plugin add popup status
    const [pluginAddPopup, setPluginAddPopup] = useState<boolean>(false)

    // API key
    const apiKeyRef = useRef<HTMLInputElement | null>(null)
    const clickedDataRef = useRef<PluginDataType>()

    const pluginSearchRef = useRef<HTMLInputElement | null>(null)

    // Handle click install
    const handleAddClick = (clickedData: PluginDataType) => {
        setPluginAddPopup(true)
        clickedDataRef.current = clickedData
    }

    // Handle click remove
    const handleRemoveClick = (clickedData: PluginDataType) => {
        console.log("Remove plugin: ", clickedData.id)
        setPluginData(prevState => 
            prevState.map(plugin => 
                plugin.id === clickedData.id ? {...plugin, isInstalled: false} : plugin
            )
        )
    }

    // Handle click popup add
    const handleClickPopupAdd = () => {
        try 
        {
            console.log("add plugin with API key: ", apiKeyRef.current?.value)
            setPluginData(prevState => prevState.map(plugin => plugin.id === clickedDataRef.current?.id ? {...plugin, isInstalled: true} : plugin ))
            setPluginAddPopup(false)
        } 
        catch (ex) 
        {
            showAlertDialog("Error", "Error adding plugin");
            setPluginAddPopup(false)
        }
    }

    // Handle search field change
    const handleSearchChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (pluginSearchRef) 
        {
            if (event.target.value === "")
            {
                setPluginDataFilter(pluginData)
            } 
            else 
            {
                setPluginDataFilter(pluginData.filter(item => item.name.toLowerCase().includes(event.target.value.toLowerCase())))
            }
        }
    }

    // Click handle update
    const handleClickUpdate = (plugin: PluginDataType) => {
        const latestVersion = versionsRef.current.filter(item => item.id === plugin.id)
        console.log("update version: ", plugin.version, " -> ", latestVersion[0].latestVersion)
    }

    useEffect(() => {
        // Load plugins to pluginData
        // Load plugin latest version to versionsRef
    }, [])

    useEffect(() => {
        if (pluginSearchRef.current)
        {
            const searchValue = pluginSearchRef.current.value || ""

            if (searchValue === "") 
            {
                setPluginDataFilter(pluginData)
            } 
            else 
            {
                setPluginDataFilter(pluginData.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase())))
            }
        }
        
    }, [pluginData])

    // Is update available
    const isUpdateAvailable = (plugin: PluginDataType): boolean => {
        const currentVersion = plugin.version
        const latestVersion = versionsRef.current.filter(item => item.id === plugin.id)

        if (semver.lt(currentVersion, latestVersion[0].latestVersion))
        {
            return true
        }
        else 
        {
            return false
        }
    }

    return (
        <div className={`container-wrapper`}>
            <span className={`title`}>settings</span>

            <div className={`plugin-setting-wrapper`}>
                <div className={`setting-container`}>
                    <div className={`title`}>
                        <span>plugins</span>
                    </div>
                    <div className='search-field-wrapper'>
                        <InputGroup type='text' color='var(--color-accent-bg)' placeholder='Search plugins' componentInputElement={pluginSearchRef} name='api_key' icon={<SearchIcon width='1.5rem' color='var(--color-main-white)'/>} onChange={handleSearchChange}/>
                    </div>
                    <div className='plugin-item-wrapper'>
                        {pluginDataFilter.map((plugin, index) => (
                            <div key={index} className='plugin-item'>
                                <div className='plugin-image-wrapper'>
                                    {plugin.image !== "" ? <img src={plugin.image} alt="" /> : <IconPluginSetting width='4rem' color='var(--color-text)'/>}
                                </div>
                                
                                <div className='plugin-details-container'>
                                    <div className='left-part'>
                                        <span className='main-title'>{plugin.name}</span>
                                        <span className='description'>{plugin.description}</span>
                                        <span className='developer'>Developer: <a href={plugin.url} target='_blank'>{plugin.developer}</a></span>
                                    </div>
                                    <div className='right-part'>
                                        {plugin.isInstalled ? (
                                            <div className='button' style={{backgroundColor: "var(--color-main-red)"}} onClick={() => handleRemoveClick(plugin)}>
                                                remove
                                            </div>
                                        ) : (
                                            <div className='button' style={{backgroundColor: plugin.enabled ? "var(--color-main-blue)" : "gray"}} onClick={() => {
                                                    if(plugin.enabled) {
                                                        handleAddClick(plugin)
                                                    }
                                            }}>
                                                install
                                            </div>
                                        )}
                                        {isUpdateAvailable(plugin) && plugin.isInstalled && (
                                            <div className='button' style={{backgroundColor: "var(--color-main-green)"}} onClick={() => handleClickUpdate(plugin)}>
                                                update
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {pluginAddPopup && (
                <div className='plugin-setting-popup-wrapper'>
                    <div className='popup-wrapper'>
                        <div className='title'>
                            <span>Add Plugin</span>
                            <div className='icon' onClick={() => setPluginAddPopup(false)}>
                                <CloseIcon width='1rem' color='var(--color-main-white)'/>
                            </div>
                        </div>
                        <div className='popup-wrapper-body'>
                            <span>Please add your API key</span>
                            <InputGroup type='text' color='var(--color-accent-bg)' placeholder='Enter API key here' componentInputElement={apiKeyRef} name='api_key' icon={<PasswordIcon width='1.5rem' color='var(--color-main-white)'/>}/>
                        </div>
                        <div className='button-container'>
                            <a href={clickedDataRef.current?.url} target='_blank'>buy a new API key</a>
                            <button onClick={() => handleClickPopupAdd()}>Add</button>
                        </div>
                    </div>
                </div>
            )}
            {alertDialogState && (
                <AlertDialog
                title={alertTitle}
                description={alertDescription}
                windowState={alertDialogState}
                setWindowState={setAlertDialogState}
                function={alertCallback}
                />
            )}
        </div>
    )
}

export default PluginSettings